import { useSearchParams } from 'react-router-dom';
import { addDays, isIsoDate, todayIso } from '../format';
import { useLocale } from '../i18n';
import type { AppMessageKey } from '../messages';

/** A date the books could hold: a year typed half-way (0002, 0202) is not a period to ask the server for. */
const plausible = (d: string): boolean => isIsoDate(d) && d >= '2000-01-01';

export type Preset = { key: 'd7' | 'd30' | 'd90' | 'd365'; days: number };

/** One meaning for "Last 30 days" on every report: today and the 29 days before it (30 days in all), and so on. */
const SPAN: Record<Preset['key'], number> = { d7: 6, d30: 29, d90: 89, d365: 364 };
/** The period buttons of a report, the first one its default. */
export const presets = (...keys: Preset['key'][]): readonly Preset[] => keys.map((key) => ({ key, days: SPAN[key] }));

/**
 * A report's period, kept in the address (?from=&to=) so a report can be linked and reloaded: preset buttons
 * plus From and To dates, the way an accounting report runs. To defaults to today, From to the first preset.
 */
export function usePeriod(presets: readonly Preset[]) {
  const today = todayIso();
  const [params, setParams] = useSearchParams();
  const toRaw = params.get('to') ?? '';
  const fromRaw = params.get('from') ?? '';
  // From after To (typed the wrong way round) is read as the same two dates in order, not quietly as another period
  const swapped = plausible(fromRaw) && plausible(toRaw) && fromRaw > toRaw;
  const toWant = swapped ? fromRaw : toRaw;
  const fromWant = swapped ? toRaw : fromRaw;
  const to = plausible(toWant) && toWant <= today ? toWant : today;
  const from = plausible(fromWant) && fromWant <= to ? fromWant : addDays(to, -presets[0]!.days);
  // both dates in one change of the address, so one never overwrites the other; a range read the wrong way round is
  // written back in order with the edit, so editing From does not bring back the old To
  const setRange = (given: { from?: string; to?: string }) => {
    const next = swapped ? { from, to, ...given } : given;
    return applyRange(next);
  };
  const applyRange = (next: { from?: string; to?: string }) =>
    setParams(
      (prev) => {
        const out = new URLSearchParams(prev);
        for (const [k, v] of Object.entries(next)) {
          if (v) out.set(k, v);
          else out.delete(k);
        }
        return out;
      },
      { replace: true },
    );
  return { from, to, today, setRange };
}

export function PeriodBar({ presets, period }: { presets: readonly Preset[]; period: ReturnType<typeof usePeriod> }) {
  const { t } = useLocale();
  const { from, to, today, setRange } = period;
  return (
    <>
      <div className="seg" role="group" aria-label={t('period')}>
        {presets.map((p) => {
          const start = addDays(today, -p.days);
          return (
            <button
              key={p.key}
              type="button"
              aria-pressed={from === start && to === today}
              onClick={() => setRange({ from: start, to: '' })}
            >
              {t(`range_${p.key}` as AppMessageKey)}
            </button>
          );
        })}
      </div>
      <label className="tool">
        <span>{t('fromDate')}</span>
        <input type="date" value={from} max={to} onChange={(e) => plausible(e.target.value) && setRange({ from: e.target.value })} />
      </label>
      <label className="tool">
        <span>{t('toDate')}</span>
        <input type="date" value={to} min={from} max={today} onChange={(e) => plausible(e.target.value) && setRange({ to: e.target.value })} />
      </label>
    </>
  );
}
